import * as Sentry from '@sentry/react';
import { signIn, signOut } from 'aws-amplify/auth';

import type { WebAuthnCeremony } from '@/features/mfa/webauthn-ceremony';

import { AuthFlowError, cognitoErrorName, mapCognitoAuthError } from './auth-flow-error';
import { confirmCognitoChallengeOnce } from './cognito-challenge';
import { cancelLoginAttempt, LoginAttempt, type MfaLoginGrant } from './login-attempt';

let operationInFlight = false;
let nativeCeremony: WebAuthnCeremony<unknown> | undefined;

export const cancelSignIn = (): void => {
  cancelLoginAttempt();
  nativeCeremony?.cancel();
  nativeCeremony = undefined;
};

export const clearCognitoSession = async (): Promise<void> => {
  cancelSignIn();
  try {
    await signOut();
  } catch (error) {
    throw mapCognitoAuthError(error);
  }
};

export const exclusiveSignIn = async <T>(action: () => Promise<T>): Promise<T> => {
  if (operationInFlight) {
    throw new AuthFlowError('session-expired');
  }
  operationInFlight = true;
  try {
    return await action();
  } finally {
    operationInFlight = false;
  }
};

export enum CustomChallenge {
  Totp = 'gamma-totp',
  Passkey = 'gamma-passkey-proof',
  Exchange = 'gamma-mfa-exchange',
}

export type SignInOutput = Awaited<ReturnType<typeof signIn>>;

export const challengeParameters = (output: SignInOutput) =>
  !output.isSignedIn && output.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
    ? output.nextStep.additionalInfo
    : undefined;

const isDone = (output: SignInOutput): boolean =>
  output.isSignedIn && output.nextStep.signInStep === 'DONE';

const exchangeProofPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[A-Za-z0-9_-]{43}$/;
const exchangeProofLocalRetentionMs = 60_000;
const exchangeClockSkewToleranceMs = 300_000;

type ExchangeChallengeRejectionReason =
  | 'unexpected-challenge'
  | 'invalid-proof'
  | 'invalid-expiry'
  | 'unexpected-completion';

const rejectExchangeChallenge = (reason: ExchangeChallengeRejectionReason): never => {
  Sentry.captureMessage('Cognito exchange challenge rejected', {
    level: 'warning',
    tags: { type: 'auth_flow', stage: 'exchange-acknowledgment', reason },
  });
  throw new AuthFlowError('unsupported-challenge');
};

const readExchangeChallenge = (output: SignInOutput, receivedAt: number) => {
  const info = challengeParameters(output);
  if (info?.challengeType !== CustomChallenge.Exchange) {
    return rejectExchangeChallenge('unexpected-challenge');
  }
  if (!exchangeProofPattern.test(info.mfaLoginProof ?? '')) {
    return rejectExchangeChallenge('invalid-proof');
  }
  const expiresAt = Number(info.expiresAt);
  if (
    !/^\d+$/.test(info.expiresAt ?? '') ||
    !Number.isSafeInteger(expiresAt) ||
    expiresAt < receivedAt - exchangeClockSkewToleranceMs ||
    expiresAt > receivedAt + exchangeProofLocalRetentionMs + exchangeClockSkewToleranceMs
  ) {
    return rejectExchangeChallenge('invalid-expiry');
  }
  return { expiresAt, proof: info.mfaLoginProof };
};

const localProofDeadline = (serverExpiresAt: number, receivedAt: number): number =>
  serverExpiresAt > receivedAt && serverExpiresAt <= receivedAt + exchangeProofLocalRetentionMs
    ? serverExpiresAt
    : receivedAt + exchangeProofLocalRetentionMs;

export const acknowledgeExchange = async (
  output: SignInOutput,
  attempt: LoginAttempt,
): Promise<MfaLoginGrant> => {
  attempt.assertCurrent();
  const receivedAt = Date.now();
  const challenge = readExchangeChallenge(output, receivedAt);
  attempt.holdProof(challenge.proof, localProofDeadline(challenge.expiresAt, receivedAt));
  const result = await confirmCognitoChallengeOnce(challenge.proof);
  attempt.assertCurrent();
  if (!isDone(result)) {
    return rejectExchangeChallenge('unexpected-completion');
  }
  return attempt.grant();
};

export const withStaleSessionRetry = async (
  action: () => Promise<SignInOutput>,
  attempt: LoginAttempt,
  canRetry: () => boolean = () => true,
): Promise<SignInOutput> => {
  try {
    return await action();
  } catch (error) {
    attempt.assertCurrent();
    if (cognitoErrorName(error) !== 'UserAlreadyAuthenticatedException' || !canRetry()) {
      throw error;
    }
    await signOut();
    attempt.assertCurrent();
    return action();
  }
};

export const ownSignInCeremony = (ceremony: WebAuthnCeremony<unknown>): void => {
  nativeCeremony = ceremony;
};

export const releaseSignInCeremony = (ceremony: WebAuthnCeremony<unknown>): void => {
  if (nativeCeremony === ceremony) {
    nativeCeremony = undefined;
  }
};
